import React from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { Sentry } from "./lib/sentry";

// Pages
import { LandingPage } from "./pages/LandingPage";
import { LoginPage, RegisterPage } from "./pages/AuthPages";
import { StudiosListPage } from "./pages/StudiosListPage";
import { StudioDetailPage } from "./pages/StudioDetailPage";
import { ClientDashboard } from "./pages/ClientDashboard";
import { StudioOwnerDashboard } from "./pages/StudioOwnerDashboard";
import { InstructorDashboard } from "./pages/InstructorDashboard";
import { ProfilePage } from "./pages/ProfilePage";
import { StudioSettingsPage } from "./pages/StudioSettingsPage";
import { StudioBookingsPage } from "./pages/StudioBookingsPage";
import { StudioClassesPage } from "./pages/StudioClassesPage";
import { StudioClassPassesPage } from "./pages/StudioClassPassesPage";
import { StudioClientsPage } from "./pages/StudioClientsPage";
import { StudioReportsPage } from "./pages/StudioReportsPage";
// MessageTemplatesPage moved to StudioSettingsPage as a tab
import { ForgotPasswordPage, ResetPasswordPage } from "./pages/PasswordResetPages";
import { BookingsPage } from "./pages/BookingsPage";
import { MembershipsPage } from "./pages/MembershipsPage";
import { FavouritesPage } from "./pages/FavouritesPage";
import { SchedulePage } from "./pages/SchedulePage";
import { WidgetPage } from "./pages/WidgetPage";
import BillingPage from "./pages/BillingPage";

// Admin Pages
import { AdminDashboardPage } from "./pages/AdminDashboardPage";
import { AdminStudiosPage } from "./pages/AdminStudiosPage";
import { AdminSubscriptionsPage } from "./pages/AdminSubscriptionsPage";
import { AdminSMSPackagesPage } from "./pages/AdminSMSPackagesPage";
import { AdminSettingsPage } from "./pages/AdminSettingsPage";
import AdminPaymentHealthPage from "./pages/AdminPaymentHealthPage";
import { AdminBillingPage } from "./pages/AdminBillingPage";

// Studio Pages
import { StudioInstructorsPage } from "./pages/StudioInstructorsPage";
import { StudioSubscriptionPage } from "./pages/StudioSubscriptionPage";
import StudioVouchersPage from "./pages/StudioVouchersPage";
import CommunicationLogPage from "./pages/CommunicationLogPage";
import StudioPOSPage from "./pages/StudioPOSPage";
import StudioHelpPage from "./pages/StudioHelpPage";

// Iter-46at: Legal / compliance pages (public — no auth guard).
import { TermsPage } from "./pages/policies/TermsPage";
import { RefundPolicyPage } from "./pages/policies/RefundPolicyPage";
import { PrivacyPolicyPage } from "./pages/policies/PrivacyPolicyPage";
import { CookiePolicyPage } from "./pages/policies/CookiePolicyPage";

// Components
import { Navbar } from "./components/Navbar";
import GlobalFooter from "./components/GlobalFooter";
import CookieConsentBanner from "./components/CookieConsentBanner";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Dashboard Router - routes to appropriate dashboard based on role
const DashboardRouter = () => {
  const { user } = useAuth();
  
  if (user?.role === 'studio_owner') {
    return <StudioOwnerDashboard />;
  }
  if (user?.role === 'super_admin') {
    return <Navigate to="/admin/dashboard" replace />;
  }
  if (user?.role === 'instructor') {
    return <InstructorDashboard />;
  }
  return <ClientDashboard />;
};

// Main Layout with Navbar
const MainLayoutWrapper = ({ children }) => (
  <>
    <Navbar />
    {children}
  </>
);

/**
 * Iter-46at: Global compliance chrome (footer + cookie banner).
 * Rendered inside <BrowserRouter> so we can hide it on routes where a legal
 * footer would break the UX (currently just the /widget/:studioId embed).
 */
const AppChrome = () => {
  const location = useLocation();
  const hide = location.pathname.startsWith('/widget/');
  if (hide) return null;
  return (
    <>
      <GlobalFooter />
      <CookieConsentBanner />
    </>
  );
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<MainLayoutWrapper><LandingPage /></MainLayoutWrapper>} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/studios" element={<MainLayoutWrapper><StudiosListPage /></MainLayoutWrapper>} />
      <Route path="/studio/:slug" element={<MainLayoutWrapper><StudioDetailPage /></MainLayoutWrapper>} />

      {/* Iter-46at: Legal / compliance — public, no auth needed */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/refund-policy" element={<RefundPolicyPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/cookies" element={<CookiePolicyPage />} />

      {/* Public Widget Route (for embedding) */}
      <Route path="/widget/:studioId" element={<WidgetPage />} />
      
      {/* Protected Client Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardRouter />
        </ProtectedRoute>
      } />
      <Route path="/bookings" element={
        <ProtectedRoute>
          <BookingsPage />
        </ProtectedRoute>
      } />
      <Route path="/memberships" element={
        <ProtectedRoute>
          <MembershipsPage />
        </ProtectedRoute>
      } />
      <Route path="/favourites" element={
        <ProtectedRoute>
          <FavouritesPage />
        </ProtectedRoute>
      } />
      <Route path="/favorites" element={<Navigate to="/favourites" replace />} />
      <Route path="/schedule" element={
        <ProtectedRoute>
          <SchedulePage />
        </ProtectedRoute>
      } />
      
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/payment-methods" element={<Navigate to="/billing" replace />} />
      <Route path="/billing" element={
        <ProtectedRoute>
          <BillingPage />
        </ProtectedRoute>
      } />

      {/* Protected Studio Owner Routes */}
      <Route path="/studio/dashboard" element={
        <ProtectedRoute allowedRoles={['studio_owner', 'super_admin']}>
          <StudioOwnerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/studio/classes" element={
        <ProtectedRoute allowedRoles={['studio_owner', 'super_admin']}>
          <StudioClassesPage />
        </ProtectedRoute>
      } />
      <Route path="/studio/instructors" element={
        <ProtectedRoute allowedRoles={['studio_owner', 'super_admin']}>
          <StudioInstructorsPage />
        </ProtectedRoute>
      } />
      <Route path="/studio/manage" element={
        <ProtectedRoute allowedRoles={['studio_owner', 'super_admin']}>
          <StudioOwnerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/studio/settings" element={
        <ProtectedRoute allowedRoles={['studio_owner', 'super_admin']}>
          <StudioSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/studio/bookings" element={
        <ProtectedRoute allowedRoles={['studio_owner', 'super_admin']}>
          <StudioBookingsPage />
        </ProtectedRoute>
      } />
      <Route path="/studio/classpasses" element={
        <ProtectedRoute allowedRoles={['studio_owner', 'super_admin']}>
          <StudioClassPassesPage />
        </ProtectedRoute>
      } />
      <Route path="/studio/clients" element={
        <ProtectedRoute allowedRoles={['studio_owner', 'super_admin']}>
          <StudioClientsPage />
        </ProtectedRoute>
      } />
      {/* Messages moved to Studio Settings tab */}
      <Route path="/studio/reports" element={
        <ProtectedRoute allowedRoles={['studio_owner', 'super_admin']}>
          <StudioReportsPage />
        </ProtectedRoute>
      } />
      <Route path="/studio/subscription" element={
        <ProtectedRoute allowedRoles={['studio_owner', 'super_admin']}>
          <StudioSubscriptionPage />
        </ProtectedRoute>
      } />
      <Route path="/studio/vouchers" element={
        <ProtectedRoute allowedRoles={['studio_owner', 'super_admin']}>
          <StudioVouchersPage />
        </ProtectedRoute>
      } />
      <Route path="/studio/communications" element={
        <ProtectedRoute allowedRoles={['studio_owner', 'super_admin']}>
          <CommunicationLogPage />
        </ProtectedRoute>
      } />
      <Route path="/studio/pos" element={
        <ProtectedRoute allowedRoles={['studio_owner', 'super_admin']}>
          <StudioPOSPage />
        </ProtectedRoute>
      } />
      <Route path="/studio/help" element={
        <ProtectedRoute allowedRoles={['studio_owner', 'super_admin']}>
          <StudioHelpPage />
        </ProtectedRoute>
      } />

      {/* Protected Instructor Routes */}
      <Route path="/instructor/dashboard" element={
        <ProtectedRoute allowedRoles={['instructor', 'studio_owner', 'super_admin']}>
          <InstructorDashboard />
        </ProtectedRoute>
      } />

      {/* Protected Super Admin Routes */}
      <Route path="/admin/dashboard" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <AdminDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/studios" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <AdminStudiosPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/subscriptions" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <AdminSubscriptionsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/billing" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <AdminBillingPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/sms-packages" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <AdminSMSPackagesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <AdminSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/payment-health" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <AdminPaymentHealthPage />
        </ProtectedRoute>
      } />
      
      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
          <div className="max-w-md text-center space-y-4">
            <h1 className="text-2xl font-semibold">Something went wrong</h1>
            <p className="text-muted-foreground">
              An unexpected error occurred. Our team has been notified.
            </p>
            <button
              onClick={() => { resetError(); window.location.reload(); }}
              data-testid="error-boundary-reload-btn"
              className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-primary text-primary-foreground hover:opacity-90"
            >
              Reload page
            </button>
          </div>
        </div>
      )}
    >
      <BrowserRouter>
        <AuthProvider>
          <div className="App min-h-screen flex flex-col">
            <div className="flex-1">
              <AppRoutes />
            </div>
            {/* Iter-46at: Global compliance chrome. `AppChrome` reads the current
                location and hides itself on /widget/* (studio embed) where the
                iframe has no room for it. */}
            <AppChrome />
            <Toaster position="top-right" richColors />
          </div>
        </AuthProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}

export default App;
